import React from 'react';
import { createRoot } from 'react-dom/client';
import { IconQuestionMark } from '@tabler/icons-react';

/**
 * Map of legacy Font Awesome names to Tabler icon names.
 * This ensures backward compatibility with existing backend data.
 */
const legacyNameMap = {
    faGraduationCap: 'IconSchool',
    faChartBar: 'IconChartBar',
    faFile: 'IconFile',
    faLeaf: 'IconLeaf',
    faMouse: 'IconMouse',
    faEuroSign: 'IconCurrencyEuro',
    faFutbol: 'IconBallFootball',
    faCheckCircle: 'IconCircleCheck',
    faHandHoldingUsd: 'IconHandHoldingDollar',
    faHeart: 'IconHeart',
    faBirthdayCake: 'IconCake',
    faCalendar: 'IconCalendar',
    faCalendarAlt: 'IconCalendarEvent',
    faMask: 'IconMask',
    faVolumeUp: 'IconVolume',
    faCoins: 'IconCoins',
    faBoxOpen: 'IconPackage',
    faFileAlt: 'IconFileDescription',
    faUser: 'IconUser',
    faUserFriends: 'IconUsers',
    faUsers: 'IconUsersGroup',
    faAward: 'IconAward',
    faProjectDiagram: 'IconHierarchy',
    faLightbulb: 'IconLamp',
    faRocket: 'IconRocket',
    faPiggyBank: 'IconPigMoney',
    faVoteYea: 'IconChecks',
    faTrophy: 'IconTrophy',
};

const commonShortAliases = {
    leaf: 'IconLeaf',
    tree: 'IconTree',
    sun: 'IconSun',
    drop: 'IconDroplet',
    droplet: 'IconDroplet',
    water: 'IconDroplet',
    sprout: 'IconSeedling',
    seedling: 'IconSeedling',
    gift: 'IconGift',
    mountain: 'IconMountain',
    flower: 'IconFlower',
    recycle: 'IconRecycle',
    wind: 'IconWind',
};

const normalizeIconName = (rawName) => {
    if (!rawName) return null;
    if (legacyNameMap[rawName]) return legacyNameMap[rawName];

    const lower = String(rawName).toLowerCase();
    if (commonShortAliases[lower]) {
        return commonShortAliases[lower];
    }

    if (rawName.startsWith('Icon')) {
        return rawName;
    }

    // Convert string like "chart-bar" or "user" to "IconChartBar" or "IconUser"
    const pascal = rawName.replace(/[-_]([a-z])/g, (_, g) => g.toUpperCase()).replace(/^[a-z]/, (g) => g.toUpperCase());

    return `Icon${pascal}`;
};

const iconCache = {};
// Der Glob zeigt auf die Peer-Dependency des Pakets (pnpm verlinkt sie nach
// packages/ui/node_modules), nicht auf das node_modules einer App.
const iconModules = import.meta.glob('../../node_modules/@tabler/icons-react/dist/esm/icons/*.mjs');

/**
 * Checks whether the given icon value is a custom image URL (absolute or
 * root-relative) instead of a Tabler icon name.
 */
export const isCustomIconUrl = (value) => typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value.trim());

/**
 * A standalone component that lazily loads a Tabler icon by its name.
 * If `name` is an image URL (http(s):// or /...), an <img> is rendered instead.
 *
 * Sizing contract:
 * - `size` given: Tabler icons and images are rendered at exactly that size.
 * - `size` undefined: Tabler icons fall back to `defaultSize` (or 24); images
 *   render at their natural dimensions, capped by the container
 *   (max-width/max-height 100%) and clipped by its border.
 *
 * @param {string} name - The name of the icon (e.g., 'IconUser', 'leaf', legacy 'faUser') or an image URL
 * @param {number} size - Optional icon size
 * @param {number} defaultSize - Optional fallback size for Tabler icons when `size` is undefined (default: 24)
 * @param {string} className - Optional CSS classes
 */
const DynamicIcon = ({ name, size, defaultSize, className, alt, style, ...iconProps }) => {
    const resolvedSize = size || defaultSize || 24;
    const isUrl = isCustomIconUrl(name);
    const targetName = isUrl ? null : normalizeIconName(name);

    // Memoize the lazy component to prevent re-creating it on every render
    const Icon = React.useMemo(() => {
        if (!targetName) return null;

        if (!iconCache[targetName]) {
            const pathKey = `../../node_modules/@tabler/icons-react/dist/esm/icons/${targetName}.mjs`;
            const loadIcon = iconModules[pathKey];

            if (loadIcon) {
                iconCache[targetName] = React.lazy(() =>
                    loadIcon().catch((err) => {
                        console.warn(`Icon "${targetName}" not found at path. Using fallback.`, err);
                        return { default: IconQuestionMark };
                    })
                );
            } else {
                console.warn(`Icon module for "${targetName}" (from raw name "${name}") not found in glob cache.`);
                iconCache[targetName] = React.lazy(() => Promise.resolve({ default: IconQuestionMark }));
            }
        }
        return iconCache[targetName];
    }, [targetName, name]);

    if (isUrl) {
        if (size) {
            return (
                <img
                    src={name.trim()}
                    width={size}
                    height={size}
                    className={className}
                    style={style}
                    alt={alt || ''}
                    draggable={false}
                />
            );
        }
        return (
            <img
                src={name.trim()}
                className={className}
                style={{ maxWidth: '100%', maxHeight: '100%', ...style }}
                alt={alt || ''}
                draggable={false}
            />
        );
    }

    if (!Icon) return null;

    return (
        <React.Suspense fallback={<span style={{ width: resolvedSize, height: resolvedSize }} />}>
            <Icon {...iconProps} className={className} style={style} size={resolvedSize} />
        </React.Suspense>
    );
};

/**
 * Register a Custom Element (Web Component) wrapper for DynamicIcon.
 * This allows using <tabler-icon name="IconUser" size="32" class="text-primary"></tabler-icon>
 * inside dangerouslySetInnerHTML or any static HTML strings.
 */
if (typeof window !== 'undefined' && !customElements.get('tabler-icon')) {
    class DynamicIconElement extends HTMLElement {
        constructor() {
            super();
            this.root = null;
        }

        connectedCallback() {
            if (!this.root) {
                this.root = createRoot(this);
            }
            this.render();
        }

        attributeChangedCallback() {
            this.render();
        }

        static get observedAttributes() {
            return ['name', 'size', 'class', 'className', 'color'];
        }

        render() {
            if (!this.root) return;

            const name = this.getAttribute('name');
            const size = this.getAttribute('size') || undefined;
            const className = this.getAttribute('class') || this.getAttribute('className') || undefined;
            const color = this.getAttribute('color') || undefined;

            this.root.render(
                <DynamicIcon
                    name={name}
                    size={size ? parseInt(size, 10) : undefined}
                    className={className}
                    color={color}
                />
            );
        }

        disconnectedCallback() {
            if (this.root) {
                // Delaying unmount to avoid race conditions during fast DOM updates
                const currentRoot = this.root;
                setTimeout(() => currentRoot.unmount(), 0);
                this.root = null;
            }
        }
    }

    customElements.define('tabler-icon', DynamicIconElement);
}

export default DynamicIcon;
